/**
 * Offline Queue Manager
 *
 * Manages offline action storage using IndexedDB for the Democratic Services PWA.
 * Provides reliable storage for petition signatures, consultation responses,
 * and draft content when the user is offline.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Design Decisions:
 * - IndexedDB chosen over localStorage for structured data, larger storage limits,
 *   and support for complex queries
 * - Actions stored with timestamps for FIFO ordering
 * - Retry counts track failed sync attempts
 * - Status field allows granular state management
 *
 * Database Schema:
 * - offlineActions: Stores queued form submissions (petitions, consultations)
 * - drafts: Stores auto-saved form drafts
 * - syncLog: Tracks sync history for debugging
 */

// =============================================================================
// CONFIGURATION
// =============================================================================

const DB_CONFIG = {
	name: 'DemocraticServicesOffline',
	version: 2, // Bumped to fix drafts store keyPath
	stores: {
		offlineActions: 'offlineActions',
		drafts: 'drafts',
		syncLog: 'syncLog',
	},
};

/**
 * Action status constants
 * Tracks the lifecycle of queued actions
 */
export const ACTION_STATUS = {
	PENDING: 'pending', // Waiting to be synced
	SYNCING: 'syncing', // Currently being synced
	SUCCESS: 'success', // Successfully synced
	FAILED: 'failed', // Sync failed after retries
	CANCELLED: 'cancelled', // User cancelled the action
};

/**
 * Action type constants
 * Identifies different types of offline actions
 */
export const ACTION_TYPES = {
	PETITION_SIGNATURE: 'petition_signature',
	PETITION_CREATE: 'petition_create',
	CONSULTATION_RESPONSE: 'consultation_response',
	DRAFT_SAVE: 'draft_save',
	// Additional action types for completeness
	NEWSLETTER_SIGNUP: 'newsletter_signup',
	VOTE_CAST: 'vote_cast',
	CALL_IN_DECISION: 'call_in_decision',
};

/**
 * Maximum retry attempts before marking action as failed
 */
const MAX_RETRY_ATTEMPTS = 3;

// =============================================================================
// DATABASE INITIALIZATION
// =============================================================================

/**
 * IndexedDB instance (singleton)
 * @type {IDBDatabase|null}
 */
let db = null;

/**
 * Promise to track initialization
 * @type {Promise<IDBDatabase>|null}
 */
let dbInitPromise = null;

/**
 * Initialize IndexedDB connection
 *
 * Creates database and object stores if they don't exist.
 * Uses a singleton pattern to avoid multiple connections.
 *
 * @returns {Promise<IDBDatabase>} The database connection
 */
export async function initDB() {
	// Return existing connection if available
	if (db) {
		return db;
	}

	// Return pending initialization if in progress
	if (dbInitPromise) {
		return dbInitPromise;
	}

	// Start initialization
	dbInitPromise = new Promise((resolve, reject) => {
		// Check for IndexedDB support
		if (!('indexedDB' in window)) {
			reject(new Error('IndexedDB not supported in this browser'));
			return;
		}

		const request = indexedDB.open(DB_CONFIG.name, DB_CONFIG.version);

		/**
     * Handle database upgrade (schema changes)
     * This runs when the database is created or version changes
     */
		request.onupgradeneeded = (event) => {
			const database = event.target.result;
			const oldVersion = event.oldVersion;

			// Version 2 upgrade: fix drafts store keyPath from 'id' to 'formId'
			if (oldVersion < 2 && database.objectStoreNames.contains(DB_CONFIG.stores.drafts)) {
				// Delete the old drafts store with wrong keyPath
				database.deleteObjectStore(DB_CONFIG.stores.drafts);
			}

			// Create offlineActions store
			// Stores petition signatures and consultation responses
			if (
				!database.objectStoreNames.contains(DB_CONFIG.stores.offlineActions)
			) {
				const actionsStore = database.createObjectStore(
					DB_CONFIG.stores.offlineActions,
					{ keyPath: 'id', autoIncrement: true },
				);

				// Index by status for efficient querying
				actionsStore.createIndex('status', 'status', { unique: false });

				// Index by type for filtering by action type
				actionsStore.createIndex('type', 'type', { unique: false });

				// Index by timestamp for ordering
				actionsStore.createIndex('timestamp', 'timestamp', { unique: false });

				// Compound index for efficient sync queries
				actionsStore.createIndex('status_timestamp', ['status', 'timestamp'], {
					unique: false,
				});

				// offlineActions store created
			}

			// Create drafts store
			// Stores auto-saved form content
			if (!database.objectStoreNames.contains(DB_CONFIG.stores.drafts)) {
				const draftsStore = database.createObjectStore(
					DB_CONFIG.stores.drafts,
					{ keyPath: 'formId' }, // Use formId to match inline helper schema
				);

				// Index by timestamp for cleanup of old drafts
				draftsStore.createIndex('timestamp', 'timestamp', { unique: false });

				// drafts store created
			}

			// Create syncLog store
			// Tracks sync attempts for debugging
			if (!database.objectStoreNames.contains(DB_CONFIG.stores.syncLog)) {
				const syncLogStore = database.createObjectStore(
					DB_CONFIG.stores.syncLog,
					{ keyPath: 'id', autoIncrement: true },
				);

				// Index by timestamp for chronological viewing
				syncLogStore.createIndex('timestamp', 'timestamp', { unique: false });

				// syncLog store created
			}
		};

		request.onsuccess = (event) => {
			db = event.target.result;

			// Handle database errors
			db.onerror = (error) => {
				// database error (silent)
			};

			// Handle unexpected close
			db.onclose = () => {
				db = null;
				dbInitPromise = null;
			};
			resolve(db);
		};

		request.onerror = (event) => {
			dbInitPromise = null;
			reject(event.target.error);
		};

		request.onblocked = () => {
			// Version upgrade blocked by another tab/connection
			dbInitPromise = null;
			reject(new Error('Database upgrade blocked. Please close other tabs and refresh.'));
		};
	});

	return dbInitPromise;
}

// =============================================================================
// DEDUPLICATION
// =============================================================================

/**
 * Check if a submission with the given ID already exists in the queue
 *
 * Prevents duplicate submissions when the same form is submitted multiple times
 * while offline (e.g., user clicks submit button twice).
 *
 * @param {string} submissionId - The unique submission ID to check
 * @returns {Promise<number|null>} The existing action ID if duplicate, null otherwise
 */
async function checkDuplicateSubmission(submissionId) {
	if (!submissionId) {
		return null;
	}

	const database = await initDB();

	return new Promise((resolve) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const request = store.openCursor();

		request.onsuccess = (event) => {
			const cursor = event.target.result;
			if (cursor) {
				const action = cursor.value;
				// Check if this action has the same submissionId
				if (
					action.submissionId === submissionId ||
					(action.data && action.data._submission_id === submissionId)
				) {
					// Found duplicate - return existing action ID
					resolve(action.id);
					return;
				}
				cursor.continue();
			} else {
				// No duplicate found
				resolve(null);
			}
		};

		request.onerror = () => {
			// On error, allow the action to be queued (fail open)
			resolve(null);
		};
	});
}

// =============================================================================
// OFFLINE ACTION QUEUE OPERATIONS
// =============================================================================

/**
 * Add an action to the offline queue
 *
 * Stores a form submission for later synchronization when online.
 *
 * @param {Object} action - The action to queue
 * @param {string} action.type - Action type (from ACTION_TYPES)
 * @param {string} action.url - Target URL for submission
 * @param {string} action.method - HTTP method (POST, PUT, etc.)
 * @param {Object} action.data - Form data to submit
 * @param {Object} [action.metadata] - Additional metadata
 * @returns {Promise<number>} The ID of the queued action
 */
export async function queueAction(action) {
	const database = await initDB();

	// Check for duplicate submission if submissionId is provided
	if (action.submissionId) {
		const isDuplicate = await checkDuplicateSubmission(action.submissionId);
		if (isDuplicate) {
			// Return existing action ID instead of creating duplicate
			return isDuplicate;
		}
	}

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);

		// Prepare action record
		const record = {
			type: action.type,
			url: action.url,
			method: action.method || 'POST',
			data: action.data,
			submissionId: action.submissionId || null,
			metadata: action.metadata || {},
			status: ACTION_STATUS.PENDING,
			timestamp: Date.now(),
			retryCount: 0,
			lastError: null,
			syncedAt: null,
		};

		const request = store.add(record);

		request.onsuccess = () => {
			const id = request.result;

			// Dispatch event for UI updates
			dispatchQueueEvent('actionQueued', { id, action: record });

			resolve(id);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Get all pending actions from the queue
 *
 * Retrieves actions that need to be synced, ordered by timestamp (FIFO).
 *
 * @param {string} [type] - Optional filter by action type
 * @returns {Promise<Array>} Array of pending actions
 */
export async function getPendingActions(type = null) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const index = store.index('status');

		const request = index.getAll(ACTION_STATUS.PENDING);

		request.onsuccess = () => {
			let actions = request.result;

			// Filter by type if specified
			if (type) {
				actions = actions.filter((action) => action.type === type);
			}

			// Sort by timestamp (oldest first for FIFO)
			actions.sort((a, b) => a.timestamp - b.timestamp);

			resolve(actions);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Get a single action by ID
 *
 * @param {number} id - The action ID
 * @returns {Promise<Object|null>} The action or null if not found
 */
export async function getAction(id) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const request = store.get(id);

		request.onsuccess = () => {
			resolve(request.result || null);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Update an action's status
 *
 * @param {number} id - The action ID
 * @param {string} status - New status (from ACTION_STATUS)
 * @param {Object} [updates] - Additional fields to update
 * @returns {Promise<void>}
 */
export async function updateActionStatus(id, status, updates = {}) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);

		// Get existing record
		const getRequest = store.get(id);

		getRequest.onsuccess = () => {
			const record = getRequest.result;
			if (!record) {
				reject(new Error(`Action ${id} not found`));
				return;
			}

			// Update record
			const updatedRecord = {
				...record,
				...updates,
				status,
			};

			if (status === ACTION_STATUS.SUCCESS) {
				updatedRecord.syncedAt = Date.now();
			}

			const putRequest = store.put(updatedRecord);

			putRequest.onsuccess = () => {
				dispatchQueueEvent('actionUpdated', {
					id,
					status,
					action: updatedRecord,
				});
				resolve();
			};

			putRequest.onerror = () => {
				reject(putRequest.error);
			};
		};

		getRequest.onerror = () => {
			reject(getRequest.error);
		};
	});
}

/**
 * Increment retry count for an action
 *
 * @param {number} id - The action ID
 * @param {string} error - Error message from last attempt
 * @returns {Promise<boolean>} True if can retry, false if max retries exceeded
 */
export async function incrementRetryCount(id, error) {
	const action = await getAction(id);

	if (!action) {
		throw new Error(`Action ${id} not found`);
	}

	const newRetryCount = action.retryCount + 1;

	if (newRetryCount >= MAX_RETRY_ATTEMPTS) {
		// Max retries exceeded - mark as failed
		await updateActionStatus(id, ACTION_STATUS.FAILED, {
			retryCount: newRetryCount,
			lastError: error,
		});
		return false;
	}

	// Update retry count and return to pending
	await updateActionStatus(id, ACTION_STATUS.PENDING, {
		retryCount: newRetryCount,
		lastError: error,
	});

	return true;
}

/**
 * Remove an action from the queue
 *
 * @param {number} id - The action ID
 * @returns {Promise<void>}
 */
export async function removeAction(id) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const request = store.delete(id);

		request.onsuccess = () => {
			dispatchQueueEvent('actionRemoved', { id });
			resolve();
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Clear all completed (synced) actions
 *
 * Removes actions that have been successfully synced.
 * Keeps failed actions for user review.
 *
 * @returns {Promise<number>} Number of actions removed
 */
export async function clearSyncedActions() {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const index = store.index('status');

		const request = index.openCursor(IDBKeyRange.only(ACTION_STATUS.SUCCESS));
		let count = 0;

		request.onsuccess = (event) => {
			const cursor = event.target.result;
			if (cursor) {
				cursor.delete();
				count++;
				cursor.continue();
			} else {
				resolve(count);
			}
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Get count of pending actions
 *
 * @returns {Promise<number>} Number of pending actions
 */
export async function getPendingCount() {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.offlineActions,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.offlineActions);
		const index = store.index('status');
		const request = index.count(ACTION_STATUS.PENDING);

		request.onsuccess = () => {
			resolve(request.result);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

// =============================================================================
// DRAFT OPERATIONS
// =============================================================================

/**
 * Save a draft
 *
 * Stores or updates form draft content for later restoration.
 *
 * @param {Object} draft - The draft to save
 * @param {string} draft.id - Unique identifier (e.g., "petition_create_123")
 * @param {string} draft.type - Draft type (petition, consultation, etc.)
 * @param {Object} draft.data - Form field data
 * @param {string} [draft.title] - Optional title for display
 * @returns {Promise<void>}
 */
export async function saveDraft(draft) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.drafts,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.drafts);

		const record = {
			id: draft.id,
			type: draft.type,
			data: draft.data,
			title: draft.title || '',
			createdAt: draft.createdAt || Date.now(),
			updatedAt: Date.now(),
		};

		const request = store.put(record);

		request.onsuccess = () => {
			dispatchQueueEvent('draftSaved', { id: draft.id, draft: record });
			resolve();
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Get a draft by ID
 *
 * @param {string} id - The draft ID
 * @returns {Promise<Object|null>} The draft or null if not found
 */
export async function getDraft(id) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.drafts,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.drafts);
		const request = store.get(id);

		request.onsuccess = () => {
			resolve(request.result || null);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Get all drafts
 *
 * @param {string} [type] - Optional filter by draft type
 * @returns {Promise<Array>} Array of drafts
 */
export async function getAllDrafts(type = null) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.drafts,
			'readonly',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.drafts);

		let request;
		if (type) {
			const index = store.index('type');
			request = index.getAll(type);
		} else {
			request = store.getAll();
		}

		request.onsuccess = () => {
			// Sort by most recently updated
			const drafts = request.result.sort((a, b) => b.updatedAt - a.updatedAt);
			resolve(drafts);
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Remove a draft
 *
 * @param {string} id - The draft ID
 * @returns {Promise<void>}
 */
export async function removeDraft(id) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.drafts,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.drafts);
		const request = store.delete(id);

		request.onsuccess = () => {
			dispatchQueueEvent('draftRemoved', { id });
			resolve();
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

/**
 * Clear old drafts
 *
 * Removes drafts older than the specified age.
 *
 * @param {number} maxAge - Maximum age in milliseconds (default: 7 days)
 * @returns {Promise<number>} Number of drafts removed
 */
export async function clearOldDrafts(maxAge = 7 * 24 * 60 * 60 * 1000) {
	const database = await initDB();
	const cutoff = Date.now() - maxAge;

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.drafts,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.drafts);
		const index = store.index('updatedAt');

		const request = index.openCursor(IDBKeyRange.upperBound(cutoff));
		let count = 0;

		request.onsuccess = (event) => {
			const cursor = event.target.result;
			if (cursor) {
				cursor.delete();
				count++;
				cursor.continue();
			} else {
				resolve(count);
			}
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

// =============================================================================
// SYNC LOG OPERATIONS
// =============================================================================

/**
 * Log a sync attempt
 *
 * @param {Object} entry - Log entry
 * @param {number} entry.actionId - Related action ID
 * @param {string} entry.type - Action type
 * @param {boolean} entry.success - Whether sync was successful
 * @param {string} [entry.error] - Error message if failed
 * @returns {Promise<void>}
 */
export async function logSyncAttempt(entry) {
	const database = await initDB();

	return new Promise((resolve, reject) => {
		const transaction = database.transaction(
			DB_CONFIG.stores.syncLog,
			'readwrite',
		);
		const store = transaction.objectStore(DB_CONFIG.stores.syncLog);

		const record = {
			actionId: entry.actionId,
			type: entry.type,
			success: entry.success,
			error: entry.error || null,
			timestamp: Date.now(),
		};

		const request = store.add(record);

		request.onsuccess = () => {
			resolve();
		};

		request.onerror = () => {
			reject(request.error);
		};
	});
}

// =============================================================================
// EVENT DISPATCHING
// =============================================================================

/**
 * Dispatch a custom event for queue changes
 *
 * Allows UI components to react to queue state changes.
 *
 * @param {string} eventName - Name of the event
 * @param {Object} detail - Event detail data
 */
function dispatchQueueEvent(eventName, detail) {
	if (typeof window !== 'undefined') {
		const event = new CustomEvent(`offlineQueue:${eventName}`, { detail });
		window.dispatchEvent(event);
	}
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize the offline queue on load
 */
if (typeof window !== 'undefined') {
	// Initialize DB when module loads
	initDB().catch((error) => {
		// Failed to initialize offline queue (silent)
	});

	// Clean up old data periodically
	window.addEventListener('load', () => {
		// Clear synced actions and old drafts on page load
		Promise.all([clearSyncedActions(), clearOldDrafts()]).catch(() => {});
	});
}
